import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { TarotCard } from './TarotCard'
import { haptic, tg } from '../lib/telegram'

export interface ZoomedCard {
  id: number
  sym: string
  rank: string
  name: string
  /** Подпись позиции в раскладе: «Прошлое», «Июль». */
  label: string
  /** Толкование именно этой карты — от модели или из справочника. */
  meaning: string
  reversed: boolean
}

interface Props {
  card: ZoomedCard | null
  reversedLabel: string
  closeLabel: string
  onClose: () => void
}

/**
 * Карта во весь экран.
 *
 * В раскладе на десять и двенадцать карт рисунок размером с ноготь, и
 * разглядеть его нельзя — а разглядывать карту людям хочется, это половина
 * удовольствия. Поэтому по нажатию карта разворачивается: крупная
 * иллюстрация, имя, позиция и толкование.
 */
export function CardZoom({ card, reversedLabel, closeLabel, onClose }: Props) {
  // Пока карта открыта, «назад» закрывает её, а не уводит с расклада:
  // системная кнопка должна отменять последнее действие, а не через одно.
  useEffect(() => {
    if (!card) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)

    const back = tg?.BackButton
    const wasVisible = back?.isVisible
    back?.onClick(onClose)
    back?.show()

    return () => {
      document.removeEventListener('keydown', onKey)
      back?.offClick(onClose)
      if (!wasVisible) back?.hide()
    }
  }, [card, onClose])

  if (!card) return null

  /*
   * Рисуем через портал в <body>, а не на месте.
   *
   * Экран расклада появляется с анимацией, а она оставляет на нём `transform`
   * (fill-mode both сохраняет последний кадр). Элемент с `transform`
   * становится системой отсчёта для `position: fixed`, и накладка
   * ограничивалась бы экраном: шапка и нижняя кнопка оставались поверх неё.
   */
  return createPortal(
    <div
      className="zoom"
      role="dialog"
      aria-modal="true"
      aria-label={card.name}
      onClick={() => {
        haptic.impact('light')
        onClose()
      }}
    >
      <div className="zoom__inner" onClick={(e) => e.stopPropagation()}>
        {card.label && <div className="zoom__label">{card.label}</div>}

        <div className="zoom__card">
          <TarotCard
            faceUp
            cardId={card.id}
            sym={card.sym}
            rank={card.rank}
            title={card.name}
            reversed={card.reversed}
            showTitle={false}
          />
        </div>

        <div className="zoom__name">
          {card.name}
          {card.reversed && <span className="zoom__reversed"> · {reversedLabel}</span>}
        </div>

        {card.meaning && <p className="zoom__meaning">{card.meaning}</p>}

        <button type="button" className="zoom__close" onClick={onClose}>
          {closeLabel}
        </button>
      </div>
    </div>,
    document.body,
  )
}
